import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import CreateUser from './CreateUser'
import useFetch from '../../hooks/useFetch'
import { roles } from '../../roles'
import Page from '../../components/Page'
import PageTitle from '../../components/PageTitle'
import ChangeUserPass from './ChangeUserPass.js'
import ConfirmDeleteUser from './ConfirmDeleteUser'
import DataTable from '../../components/DataTable'
import Button from '@mui/material/Button'
import AddIcon from '@mui/icons-material/Add'
import KeyIcon from '@mui/icons-material/Key'
import DeleteIcon from '@mui/icons-material/Delete'
import Box from '@mui/material/Box'
import useIsMobile from '../../hooks/useIsMobile'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'

export default function Users(props) {
    const { t } = useTranslation()
    const { setAuth } = props
    const [showCreateUser, setShowCreateUser] = useState(false)
    const [showChangePass, setShowChangePass] = useState(false)
    const [showDeleteUser, setShowDeleteUser] = useState(false)
    const [targetUserID, setTargetUserID] = useState(0)
    const [targetUser, setTargetUser] = useState('')
    const { isPending, fetchResource } = useFetch({ setAuth });
    const isMobile = useIsMobile();
    const [data, setData] = useState([]);

    const columns = [
        {
            field: 'id',
            headerName: t('users.columns.id'),
            width: 70
        },
        {
            field: 'username',
            headerName: t('users.columns.username'),
            minWidth: 150,
            flex: 1
        },
        {
            field: 'role',
            headerName: t('users.columns.role'),
            width: 108
        }
    ]

    // A user, on a phone. The id column is for reconciling against the database,
    // which is not something anyone does from a phone, so it goes.
    const renderUserCard = row => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Typography sx={{ flex: 1, fontWeight: 500 }} noWrap>{row.username}</Typography>
            {/* A pill, because the role is a state rather than an action -- the
                one place a 999px radius still means something here. */}
            <Chip label={row.role} size='small' variant='outlined' sx={{ flex: 'none', borderRadius: '999px' }} />
        </Box>
    )

    const rows = (data || []).map(user => {
        const { id, username, role } = user
        const row = {
            id,
            username,
            role: roles[role]
        }
        return row
    })

    function handleEnterChangePass() {
        const usernameObj = rows.filter(row => row.id === targetUserID)
        const username = usernameObj[0].username
        setTargetUser(username)
        setShowChangePass(true)
    }

    function handleDeleteUser() {
        const usernameObj = rows.filter(row => row.id === targetUserID)
        const username = usernameObj[0].username
        setTargetUser(username)
        setShowDeleteUser(true)
    }

    function handleSelectChange(e) {
        setTargetUserID(e[0])
    }

    useEffect(() => {
        async function fetchUsers() {            
            try {
                const response = await fetchResource({ method: 'GET', resource: 'users', loadingKey: 'users' });
                if (response.error) throw new Error(response.error);
                setData(response.users);
            } catch (error) {
                alert(t('users.fetchListError', { message: error.message }));
            }
        }
        fetchUsers();
      }, []);

    return (
        <Page>
            <PageTitle>{t('users.title')}</PageTitle>
            <DataTable
                columns={columns}
                rows={rows}
                handleSelectChange={handleSelectChange}
                renderCard={renderUserCard}
                // Controlled, so the card list shows the same user the page acts
                // on. 0 is this screen's "nobody", not a row id.
                selectionModel={targetUserID ? [targetUserID] : []}
                loading={isPending('users')}
            />
            {/* On a phone this was a nowrap row holding a two-button column, so
                "Alter password" ended up on a second line pushed against the
                bottom edge of the screen. Stacked and full width below the
                breakpoint, primary first and the destructive one furthest from
                the thumb; the desktop row is unchanged. */}
            <Box
                sx={{
                    flex: 'none',
                    display: 'flex',
                    gap: 1.5,
                    mt: 1,
                    flexDirection: isMobile ? 'column' : 'row-reverse',
                    justifyContent: 'space-between'
                }}
            >
                <Box sx={{ display: 'flex', gap: 1.5, flexDirection: isMobile ? 'column' : 'row-reverse' }}>
                    {/* The one primary on this page. */}
                    <Button
                        variant='contained'
                        startIcon={<AddIcon />}
                        onClick={() => setShowCreateUser(true)}
                        fullWidth={isMobile}
                    >
                        {t('users.newUser')}
                    </Button>
                    <Button
                        variant='outlined'
                        color='inherit'
                        startIcon={<KeyIcon />}
                        onClick={event => handleEnterChangePass(event)}
                        disabled={!targetUserID}
                        fullWidth={isMobile}
                    >
                        {t('users.alterPassword')}
                    </Button>
                </Box>

                {/* Outlined and red. Filled meant this sat at the same weight as
                    "New user" while doing the opposite thing -- and once primary
                    became the accent, it wore it. */}
                <Button
                    variant='outlined'
                    color='error'
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteUser}
                    disabled={!targetUserID}
                    fullWidth={isMobile}
                >
                    {t('common.delete')}
                </Button>
            </Box>

            <ConfirmDeleteUser
                showDeleteUser={showDeleteUser}
                setShowDeleteUser={setShowDeleteUser}
                fetchResource={fetchResource}
                targetUser={targetUser}
            />
            <CreateUser
                showCreateUser={showCreateUser}
                setShowCreateUser={setShowCreateUser}
                fetchResource={fetchResource}
            />
            <ChangeUserPass
                showChangePass={showChangePass}
                setShowChangePass={setShowChangePass}
                targetUser={targetUser}
                fetchResource={fetchResource}
            />
        </Page>
        // )
    )
}
